import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Box, Tabs, Tab, Alert, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import UserTable from './UserTable';
import FileAndReportManager from './FileAndReportManager';
import CountyOnboarding from './CountyOnboarding';
import CatalogPublishing from './CatalogPublishing';
import MunicipalityAssignments from './MunicipalityAssignments';
import SourceConfig from './SourceConfig';
import ScrapingManager from './ScrapingManager';
import ScrapingConfigEditor from './ScrapingConfigEditor';
import PipelineTraceViewer from './PipelineTraceViewer';
import ReportPublishing from './ReportPublishing';
import { setSentryAdminTab } from '../../utils/sentryTracking';
import { isSessionExpired, subscribeSessionExpired } from '../../utils/sessionAuth';

const AdminPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [sessionExpired, setSessionExpired] = useState(isSessionExpired());

  useEffect(() => {
    return subscribeSessionExpired(() => setSessionExpired(true));
  }, []);

  useEffect(() => {
    setSentryAdminTab(selectedTab);
  }, [selectedTab]);

  const handleClose = () => {
    navigate('/app/map');
  };

  const handleChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Allow vertical mouse wheel to scroll the tab bar horizontally
  const handleTabsWheel = (event) => {
    if (!event.deltaY) return;
    const scroller = event.currentTarget.querySelector('.MuiTabs-scroller');
    if (scroller) {
      event.preventDefault();
      scroller.scrollLeft += event.deltaY;
    }
  };

  const tabsBar = (
    <Box sx={{ borderBottom: 1, borderColor: 'divider' }} onWheel={handleTabsWheel}>
      <Tabs
        value={selectedTab}
        onChange={handleChange}
        aria-label="admin tabs"
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="User Management" />
        <Tab label="County Onboarding" />
        <Tab label="Catalog publishing" />
        <Tab label="Municipality Assignments" />
        <Tab label="Source Configuration" />
        <Tab label="Scraping" />
        <Tab label="Scraping Config" />
        <Tab label="File and Report Management" />
        <Tab label="Pipeline Trace" />
        <Tab label="Report Publishing" />
      </Tabs>
    </Box>
  );

  const tabContent = (
    <>
      {sessionExpired && (
        <Alert
          severity="warning"
          sx={{ m: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/login')}>
              Log in
            </Button>
          }
        >
          Session expired — please log in again.
        </Alert>
      )}
      {selectedTab === 0 && <UserTable />}
      {selectedTab === 1 && <CountyOnboarding />}
      {selectedTab === 2 && <CatalogPublishing />}
      {selectedTab === 3 && <MunicipalityAssignments />}
      {selectedTab === 4 && <SourceConfig />}
      {selectedTab === 5 && <ScrapingManager />}
      {selectedTab === 6 && <ScrapingConfigEditor />}
      {selectedTab === 7 && <FileAndReportManager />}
      {selectedTab === 8 && <PipelineTraceViewer />}
      {selectedTab === 9 && <ReportPublishing />}
    </>
  );

  if (embedded) {
    return (
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: '#ffffff' }}>
        {tabsBar}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
          {tabContent}
        </Box>
      </Box>
    );
  }

  return (
    <Dialog open={true} onClose={handleClose} fullWidth maxWidth="xl" sx={{ zIndex: 1600 }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Admin Panel
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {tabsBar}
        {tabContent}
      </DialogContent>
    </Dialog>
  );
};

export default AdminPage;